import React, { useCallback, useEffect, useState } from "react";
import { ClockCounterClockwise, CaretDown, ArrowsClockwise, ArrowRight } from "@phosphor-icons/react";
import { t } from "../i18n";
import { listApplyLog } from "../lib/api";

const SOURCE_LABEL = {
  audit: "Audit",
  rejection: "Rejection",
  shopify_scan: "Shopify scan",
  theme: "Theme",
};

/** Apply log panel: shows the most recent corrections pushed to Shopify (audit trail). */
export default function ApplyLogPanel({ lang, connectionId }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listApplyLog(connectionId ? { connection_id: connectionId, limit: 100 } : { limit: 100 });
      setEntries(list || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [connectionId]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  return (
    <section className="border border-neutral-900 bg-white" data-testid="apply-log-panel">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 border-b border-neutral-900 bg-neutral-900 text-white text-left ma-trans hover:bg-neutral-800"
        data-testid="apply-log-toggle"
      >
        <ClockCounterClockwise size={14} weight="fill" />
        <div className="text-xs uppercase tracking-[0.2em] font-mono">
          {t(lang, "apply_log_title")}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] font-mono opacity-70" data-testid="apply-log-count">
            {entries.length}
          </span>
          <CaretDown size={11} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div data-testid="apply-log-body">
          <div className="flex items-center gap-2 px-4 py-2 bg-neutral-50 border-b border-neutral-200">
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              className="inline-flex items-center gap-1.5 border border-neutral-900 bg-white text-neutral-900 hover:bg-neutral-100 disabled:opacity-60 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ma-trans"
              data-testid="apply-log-refresh"
            >
              <ArrowsClockwise size={11} className={loading ? "animate-spin" : ""} />
              {t(lang, "apply_log_refresh")}
            </button>
            {entries.length > 0 && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/shopify/apply_log/export.pdf${connectionId ? `?connection_id=${connectionId}` : ""}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 border border-red-700 bg-red-700 text-white hover:bg-red-800 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ma-trans"
                data-testid="apply-log-export-pdf"
              >
                ⬇ {t(lang, "apply_log_export_pdf")}
              </a>
            )}
            <span className="text-[10px] font-mono text-neutral-500 ml-auto">
              {t(lang, "apply_log_hint")}
            </span>
          </div>
          {entries.length === 0 ? (
            <div className="px-4 py-8 text-center text-[11px] font-mono text-neutral-500" data-testid="apply-log-empty">
              {t(lang, "apply_log_empty")}
            </div>
          ) : (
            <div className="max-h-96 overflow-auto">
              {entries.map((e) => (
                <div
                  key={e.id}
                  className="border-t border-neutral-100 px-4 py-2.5 grid grid-cols-12 gap-3"
                  data-testid={`apply-log-entry-${e.id}`}
                >
                  <div className="col-span-2 text-[10px] font-mono text-neutral-500">
                    {new Date(e.applied_at).toLocaleString()}
                    <div className="mt-0.5 inline-flex items-center gap-1 border border-neutral-300 bg-neutral-100 px-1.5 py-0.5 uppercase tracking-widest text-[9px] text-neutral-700">
                      {SOURCE_LABEL[e.source] || e.source}
                    </div>
                  </div>
                  <div className="col-span-2 text-[10px] font-mono text-neutral-700 break-words">
                    <div className="uppercase tracking-widest text-neutral-500 text-[9px]">{e.target_type}</div>
                    <div className="break-all" title={e.target_id || ""}>#{e.target_id || "—"}</div>
                    <div className="mt-0.5 text-neutral-500 truncate" title={e.field}>{e.field}</div>
                  </div>
                  <div className="col-span-4">
                    <div className="border border-red-300 bg-red-50 p-1.5">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-red-800">{t(lang, "original")}</div>
                      <div className="text-[10px] font-mono text-red-900 line-clamp-2 break-words">{e.original}</div>
                    </div>
                  </div>
                  <div className="col-span-1 flex items-center justify-center text-neutral-400">
                    <ArrowRight size={12} weight="bold" />
                  </div>
                  <div className="col-span-3">
                    <div className="border border-emerald-400 bg-emerald-50 p-1.5">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-emerald-800">{t(lang, "suggested")}</div>
                      <div className="text-[10px] font-mono text-emerald-900 line-clamp-2 break-words">{e.suggested}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
